/* eslint-disable @typescript-eslint/no-explicit-any */
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  useAddPaymentMethod,
  useCustomerPortal,
  useDeletePaymentMethod,
  useGetActiveSubscriptionPlans,
  useGetPaymentMethods,
  useRevokeCancellation,
  useRevokeDowngrade,
  useUpdateDefaultPaymentMethod,
} from "@/hooks/use-subscription";
import BillingHistoryTable from "@/pages/billing/billing-history-table";
import {
  b2bPlanOrder,
  b2cPlanOrder,
  PlanCategory,
} from "@/pages/billing/pricing-data";
import {
  IActiveSubscriptionPlan,
  IBillingDetails,
  IPaymentMethod,
  IPurchasePlan,
  PaymentInterval,
  SubscriptionStatus,
} from "@/types/subscription.types";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Clock,
  CreditCard,
  Loader2,
  Package,
  Plus,
  ShoppingCart,
  Undo2,
} from "lucide-react";
import Trash from "@/components/icons/trash";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { getCountryName } from "@/lib/country-names";
import { extractErrorMessage } from "@/lib/utils";
import { BuyMoreMailboxesDialog } from "@/pages/billing/buy-more-mailboxes";
import BuyMoreTestsDialog from "@/pages/billing/buy-more-tests-dialog";
import UpgradePlanDialog from "@/pages/billing/upgrade-plan";
import { PaymentIntervalWithoutCustom2 } from "@/stores/downgrade-store";
import { authProviderIcons } from "@/pages/mailbox/esp-providers";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

export const Route = createFileRoute("/_private/billing/")({
  component: BillingPage,
});

function BillingPage() {
  return (
    <div className="flex w-full flex-col">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
        <div className="flex items-center justify-between w-full gap-3">
          <h1 className="page-heading">Billing</h1>
          <BuyMoreTestsDialog />
        </div>
      </div>

      <div className="space-y-6">
        <CurrentPlans />

        <PaymentInfo />

        <BillingDetails />

        <BillingHistoryTable />
      </div>
    </div>
  );
}

const CurrentPlans = () => {
  const { data: activePlans } = useGetActiveSubscriptionPlans();

  // Group and sort plans: monthly plans first (sorted by expiry), then others
  const groupedPlans = useMemo(() => {
    if (!activePlans || activePlans.length === 0) return { monthly: [], others: [] };

    const monthly: IActiveSubscriptionPlan[] = [];
    const others: IActiveSubscriptionPlan[] = [];

    activePlans.forEach((subscription) => {
      if (subscription.interval === PaymentInterval.MONTHLY) {
        monthly.push(subscription);
      } else {
        others.push(subscription);
      }
    });

    // Sort monthly plans by expiry date (earliest first)
    monthly.sort((a, b) => {
      const dateA = new Date(a.endDate).getTime();
      const dateB = new Date(b.endDate).getTime();
      return dateA - dateB;
    });

    return { monthly, others };
  }, [activePlans]);

  return (
    <div className="space-y-2">
      <div className="flex flex-col">
        <h2 className="text-lg font-medium">Current Plans</h2>
        <p className="text-sm text-muted-foreground">
          View and track your current plans.
        </p>
      </div>

      {!activePlans || activePlans.length === 0 ? (
        <Card className="p-6 py-10 flex flex-col items-center justify-center text-center bg-secondary">
          <div className="h-12 aspect-square border rounded-lg bg-background flex items-center justify-center mb-3">
            <Package className="size-6 text-foreground" />
          </div>
          <h3 className="text-base font-medium">No Active Plans</h3>
          <p className="text-sm text-muted-foreground max-w-md">
            You don't have any active subscription plans at the moment.
          </p>
          <Link to="/plans" className="mt-4">
            <Button variant="default">
              <ShoppingCart className="h-4 w-4" /> Buy a Plan
            </Button>
          </Link>
        </Card>
      ) : (
        <div className="flex flex-col gap-2">
          {/* Render monthly plans grouped together */}
          {groupedPlans.monthly.length > 0 && (
            <div>
              <div className="font-medium mb-1">
                <span>Monthly Billed</span>
              </div>
              <div className="flex flex-col gap-2">
                {groupedPlans.monthly.map((subscription) =>
                  subscription.plans.map((plan, index) => (
                    <CurrentPlanCard
                      key={`${subscription._id}-${index}`}
                      plan={plan}
                      subscription={subscription}
                    />
                  ))
                )}
              </div>
            </div>
          )}

          {/* Render other plans (non-monthly) */}
          {groupedPlans.others.map((subscription) => (
            <div key={subscription._id}>
              <div className="font-medium mb-1">
                <span>{subscription.interval} Billed</span>
              </div>
              <div className="flex flex-col gap-2">
                {subscription.plans.map((plan, index) => (
                  <CurrentPlanCard
                    key={index}
                    plan={plan}
                    subscription={subscription}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Helper function to format ESP type name for display
const formatEspTypeName = (espType: string): string => {
  const espTypeStr = String(espType).trim();
  // Map ESP types to display names
  const displayNames: Record<string, string> = {
    "Gmail": "Gmail",
    "gmail": "Gmail",
    "Outlook": "Outlook",
    "outlook": "Outlook",
    "Yahoo": "Yahoo",
    "yahoo": "Yahoo",
    "Google Workspace": "Google Workspace",
    "Google-Workspace": "Google Workspace",
    "google_workspace": "Google Workspace",
    "Microsoft 365": "Microsoft 365",
    "Microsoft365": "Microsoft 365",
    "microsoft_365": "Microsoft 365",
  };
  return displayNames[espTypeStr] || espTypeStr;
};

// Helper function to parse seed counts from plan name
// Plan name format: "Sender Profile - Gmail - 3000 - Outlook - 2000 seeds"
const parseSeedCountsFromPlanName = (planName: string, espTypes: string[]): Record<string, number> | null => {
  if (!planName || !espTypes || espTypes.length === 0) return null;
  
  const result: Record<string, number> = {};
  
  // Try to extract seed counts from plan name
  // Look for pattern: ESP_TYPE - NUMBER (with various separators)
  espTypes.forEach((espType: string) => {
    const espTypeName = formatEspTypeName(espType);
    const espTypeLower = espTypeName.toLowerCase();
    const espTypeOriginal = espType.toLowerCase();
    
    // Escape special regex characters
    const escapeRegex = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    
    // Try different patterns to match ESP type and number
    // Pattern 1: "Gmail - 3000" (most common format)
    // Pattern 2: "Gmail 3000" (without dash)
    // Pattern 3: Try with original ESP type name variations
    const patterns = [
      // Format: "Gmail - 3000" or "Gmail - 3000 seeds"
      new RegExp(`${escapeRegex(espTypeLower)}\\s*-\\s*(\\d+)(?:\\s*seeds?)?`, 'i'),
      new RegExp(`${escapeRegex(espTypeLower)}\\s+(\\d+)(?:\\s*seeds?)?`, 'i'),
      // Try with original ESP type name
      new RegExp(`${escapeRegex(espTypeOriginal)}\\s*-\\s*(\\d+)(?:\\s*seeds?)?`, 'i'),
      new RegExp(`${escapeRegex(espTypeOriginal)}\\s+(\\d+)(?:\\s*seeds?)?`, 'i'),
      // Try with exact case match
      new RegExp(`${escapeRegex(espTypeName)}\\s*-\\s*(\\d+)(?:\\s*seeds?)?`, 'i'),
      new RegExp(`${escapeRegex(espType)}\\s*-\\s*(\\d+)(?:\\s*seeds?)?`, 'i'),
    ];
    
    for (const pattern of patterns) {
      const match = planName.match(pattern);
      if (match && match[1]) {
        const seedCount = parseInt(match[1], 10);
        if (!isNaN(seedCount) && seedCount > 0) {
          result[espType] = seedCount;
          break;
        }
      }
    }
  });
  
  // Only return result if we found seed counts for all ESP types
  if (Object.keys(result).length === espTypes.length) {
    return result;
  }
  
  return null;
};

const CurrentPlanCard = ({
  plan,
  subscription,
}: {
  plan: IPurchasePlan;
  subscription: IActiveSubscriptionPlan;
}) => {
  // Check if this is a seed list plan
  const isSeedListPlan = plan.planId?.isOnDemandSenderProfile || false;
  const espTypes = plan.planId?.espTypes || plan.planId?.senderProfileMetadata?.espTypes || [];
  const seedCountPerSender = plan.planId?.seedCountPerSender || plan.planId?.senderProfileMetadata?.baseSeedCount || 0;

  // Calculate expiry/renewal date info
  const endDate = new Date(subscription.endDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  endDate.setHours(0, 0, 0, 0);
  const daysUntilRenewal = Math.ceil(
    (endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
  );
  const isExpiringSoon = daysUntilRenewal <= 7 && daysUntilRenewal >= 0;
  const formattedDate = endDate.toLocaleDateString("en-GB", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  const renderPlanName = () => {
    if (plan.planId) {
      if (isSeedListPlan && espTypes.length > 0) {
        // Show ESP type names like "Gmail seeds" or "Microsoft 365 seeds"
        const espTypeNames = espTypes.map((espType: string) => {
          const displayName = formatEspTypeName(espType);
          return `${displayName} seeds`;
        });
        return espTypeNames.join(", ");
      }
      return `${plan.planId.category} ${plan.planId.planType}`;
    }
    if (plan.isCustomPlan) {
      return `Custom ${plan.addonPlanId?.maxEmailsPerDay}`;
    }
    return "Unknown";
  };

  const renderStatus = () => {
    if (plan.pendingRequests?.cancellation) {
      return (
        <Badge variant="soft" color="yellow">
          Cancellation Pending for {plan.pendingRequests.cancellation.quantity}{" "}
          {isSeedListPlan ? "sender profile" : "mailbox"}
          {plan.pendingRequests.cancellation.quantity > 1 ? "s" : ""}
        </Badge>
      );
    }
    if (plan.pendingRequests?.downgrade) {
      return (
        <Badge variant="soft" color="yellow">
          Downgrade Pending for {plan.pendingRequests.downgrade.quantity}{" "}
          {isSeedListPlan ? "sender profile" : "mailbox"}
          {plan.pendingRequests.downgrade.quantity > 1 ? "s" : ""}
        </Badge>
      );
    }
    return (
      <Badge variant="soft" color="green">
        Active
      </Badge>
    );
  };

  // Helper function to get ESP type icon
  const getEspTypeIcon = (espType: string) => {
    const espTypeStr = String(espType).trim().toLowerCase();
    // Try multiple variations: normalized (with underscores), with dashes, original lowercase
    const normalizedType = espTypeStr.replace(/[\s-]+/g, "_");
    const dashType = espTypeStr.replace(/[\s_]+/g, "-");
    
    // Handle specific mappings for common ESP types
    const typeMap: Record<string, string> = {
      "gmail": "gmail",
      "outlook": "outlook",
      "yahoo": "yahoo",
      "google-workspace": "google_workspace",
      "google workspace": "google_workspace",
      "googleworkspace": "google_workspace",
      "microsoft365": "microsoft_365",
      "microsoft 365": "microsoft_365",
    };
    
    const mappedType = typeMap[espTypeStr] || normalizedType;
    
    return authProviderIcons[mappedType] 
      || authProviderIcons[normalizedType]
      || authProviderIcons[dashType]
      || authProviderIcons[espTypeStr]
      || null;
  };

  // Calculate seeds per ESP type (use seedVolumeByEspType from API when present)
  const seedsPerEspType = useMemo(() => {
    if (!isSeedListPlan) {
      return {};
    }
    // Prefer API-provided distribution when available
    const byEsp = plan.seedVolumeByEspType;
    if (byEsp && typeof byEsp === "object" && Object.keys(byEsp).length > 0) {
      return { ...byEsp };
    }
    if (espTypes.length === 0) {
      return {};
    }
    // Try to parse from plan name
    const planName = plan.planId?.planName || "";
    const parsedCounts = parseSeedCountsFromPlanName(planName, espTypes);
    if (parsedCounts) {
      return parsedCounts;
    }
    // Fallback: divide seedCountPerSender equally if available
    if (seedCountPerSender === 0) {
      return {};
    }
    const seedsPerType = Math.floor(seedCountPerSender / espTypes.length);
    const remaining = seedCountPerSender % espTypes.length;
    const result: Record<string, number> = {};
    espTypes.forEach((espType: string, index: number) => {
      result[espType] = seedsPerType + (index < remaining ? 1 : 0);
    });
    return result;
  }, [isSeedListPlan, espTypes, seedCountPerSender, plan.planId?.planName, plan.seedVolumeByEspType]);

  return (
    <Card className="">
      <CardHeader className="p-4 flex flex-col lg:flex-row lg:items-center gap-3 w-full text-left space-y-0">
        <div className="h-10 aspect-square w-fit border rounded-md bg-secondary flex items-center justify-center lg:flex-shrink-0">
          <Package className="size-5 text-muted-foreground" />
        </div>
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between w-full gap-4">
          <div className="space-y-1 flex-1">
            <div className="text-sm font-medium flex flex-col sm:flex-row sm:items-center gap-2">
              <div className="space-x-1">{renderPlanName()}</div>
              {renderStatus()}
              {isExpiringSoon ? (
                <Badge
                  variant="soft"
                  color="red"
                  className="text-xs font-normal flex items-center gap-1 w-fit"
                >
                  <Clock className="size-3" />
                  Will expire on {formattedDate}
                </Badge>
              ) : (
                <span className="text-xs text-muted-foreground font-normal flex items-center gap-1">
                  <Clock className="size-3" />
                  Renews on {formattedDate}
                </span>
              )}
            </div>

            {isSeedListPlan ? (
              // Display sender profile usage and ESP types with seed counts
              <div className="space-y-2">
                {/* Sender Profile Usage */}
                <div className="flex gap-2 w-full items-center">
                  <div className="flex-1 lg:max-w-[200px]">
                    <Progress
                      value={
                        plan.maxSenderProfilesMonthly > 0
                          ? (plan.usedSenderProfilesMonthly / plan.maxSenderProfilesMonthly) * 100
                          : 0
                      }
                      className="h-1.5"
                    />
                  </div>
                  <div className="flex gap-1 text-xs font-medium text-muted-foreground whitespace-nowrap">
                    <span className="text-foreground">
                      {plan.usedSenderProfilesMonthly || 0}
                    </span>
                    <span>/</span>
                    <span>{plan.maxSenderProfilesMonthly || plan.quantity || 0}</span>
                    <span className="text-muted-foreground ml-1">sender profiles</span>
                  </div>
                </div>
                {/* ESP Types and Seed Counts */}
                {Object.keys(seedsPerEspType).length > 0 && (
                  <div className="flex flex-wrap gap-3 items-center">
                    {Object.entries(seedsPerEspType).map(([espType, seedCount], index) => {
                      const iconMapping = getEspTypeIcon(espType);
                      const displayName = formatEspTypeName(espType);
                      return (
                        <Tooltip key={`${espType}-${index}`}>
                          <TooltipTrigger asChild>
                            <div className="flex items-center gap-1.5 text-xs">
                              {iconMapping ? (
                                <div className="flex items-center">
                                  {iconMapping.icon}
                                </div>
                              ) : (
                                <span className="text-muted-foreground">{displayName}</span>
                              )}
                              <span className="font-medium text-foreground">
                                {seedCount} seeds
                              </span>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{displayName}: {seedCount} seeds per sender</p>
                          </TooltipContent>
                        </Tooltip>
                      );
                    })}
                    {(seedCountPerSender > 0 || Object.keys(seedsPerEspType).length > 0) && (
                      <span className="text-xs text-muted-foreground">
                        ({(seedCountPerSender > 0 ? seedCountPerSender : Object.values(seedsPerEspType).reduce((a, b) => a + b, 0))} total seeds per sender)
                      </span>
                    )}
                  </div>
                )}
              </div>
            ) : (
              // Display mailbox progress for regular plans
              <div className="flex gap-2 w-full items-center">
                <div className="flex-1 lg:max-w-[200px]">
                  <Progress
                    value={(plan.assignedMailboxes.length / plan.quantity) * 100}
                    className="h-1.5"
                  />
                </div>
                <div className="flex gap-1 text-xs font-medium text-muted-foreground whitespace-nowrap">
                  <span className="text-foreground">
                    {plan.assignedMailboxes.length}
                  </span>
                  <span>/</span>
                  <span>{plan.quantity}</span>
                </div>
              </div>
            )}
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 lg:gap-3">
            <CuurentPlanCardActions plan={plan} subscription={subscription} />
          </div>
        </div>
      </CardHeader>
    </Card>
  );
};

function CuurentPlanCardActions({
  plan,
  subscription,
}: {
  plan: IPurchasePlan;
  subscription: IActiveSubscriptionPlan;
}) {
  const { mutateAsync: revokeCancellation, isPending: isRevokingCancellation } =
    useRevokeCancellation();

  const { mutateAsync: revokeDowngrade, isPending: isRevokingDowngrade } =
    useRevokeDowngrade();

  const handleRevokeDowngrade = async () => {
    if (plan.pendingRequests?.downgrade) {
      try {
        const result = await revokeDowngrade(
          plan.pendingRequests.downgrade._id
        );

        toast.success(result.message);

        window.cioanalytics?.track("downgrade_revoked", {
          plan: plan.planId?.planType,
          subscriptionId: subscription._id,
        });
      } catch (error) {
        const message = extractErrorMessage(
          error,
          "Failed to revoke downgrade request"
        );
        toast.error(message);
      }
    }
  };

  const handleRevokeCancellation = async () => {
    if (plan.pendingRequests?.cancellation) {
      try {
        const result = await revokeCancellation(
          plan.pendingRequests.cancellation._id
        );

        toast.success(result.message);

        window.cioanalytics?.track("cancellation_revoked", {
          plan: plan.planId?.planType,
          subscriptionId: subscription._id,
        });
      } catch (error) {
        const message = extractErrorMessage(
          error,
          "Failed to revoke cancellation request"
        );
        toast.error(message);
      }
    }
  };

  const isHighestPlanAndCycle = useMemo(() => {
    if (subscription.interval === PaymentInterval.ANNUAL) {
      const category = plan.planId?.category as
        | PlanCategory.B2B
        | PlanCategory.B2C;
      const isB2B = category === PlanCategory.B2B;

      const planOrder = isB2B ? b2bPlanOrder : b2cPlanOrder;

      const planIndex = planOrder.findIndex((p) => p === plan.planId?.planType);

      const highestPlanIndex = planOrder.length - 1;

      if (category === PlanCategory.B2B) {
        return planIndex === highestPlanIndex;
      }

      return (
        planIndex === highestPlanIndex &&
        subscription.interval === PaymentInterval.ANNUAL
      );
    }
    return false;
  }, [plan, subscription]);

  // Check if this is a seed list plan
  const isSeedListPlan = plan.planId?.isOnDemandSenderProfile || false;

  // Check if this is a free trial plan
  const isFreeTrial = useMemo(() => {
    const planName = plan.planId?.planName || "";
    return planName.toLowerCase().includes("trial") || planName.toLowerCase().includes("free trial");
  }, [plan.planId?.planName]);

  const isPendingCancellation = plan.pendingRequests?.cancellation;
  const isPendingDowngrade = plan.pendingRequests?.downgrade;
  const canUpgrade =
    !plan.isCustomPlan &&
    !isHighestPlanAndCycle &&
    subscription.status !== SubscriptionStatus.PENDING;
  const canBuyMore =
    subscription.isLatestInvoicePaid ||
    subscription.status !== SubscriptionStatus.PENDING;

  // 1. Undo cancellation
  if (isPendingCancellation) {
    return (
      <Button
        variant="outline"
        className="w-full sm:w-auto"
        onClick={handleRevokeCancellation}
        disabled={isRevokingCancellation}
      >
        {isRevokingCancellation ? (
          <Loader2 className="size-4 animate-spin" />
        ) : (
          <Undo2 className="size-4" />
        )}
        Revoke Cancellation
      </Button>
    );
  }

  // 2. Undo downgrade
  if (isPendingDowngrade) {
    return (
      <Button
        variant="outline"
        className="w-full sm:w-auto"
        onClick={handleRevokeDowngrade}
        disabled={isRevokingDowngrade}
      >
        {isRevokingDowngrade ? (
          <Loader2 className="size-4 animate-spin" />
        ) : (
          <Undo2 className="size-4" />
        )}
        Revoke Downgrade
      </Button>
    );
  }

  // 3. For free trial plans, show "Buy Now" button that redirects to plans page
  if (isFreeTrial) {
    return (
      <Link to="/plans">
        <Button variant="default" className="w-full sm:w-auto">
          <ShoppingCart className="h-4 w-4 mr-2" />
          Buy Now
        </Button>
      </Link>
    );
  }

  // 4. Default actions: upgrade and buy more (only for non-seed list plans)
  return (
    <>
      {canUpgrade && !isSeedListPlan && (
        <UpgradePlanDialog
          plan={plan}
          subscriptionId={subscription._id}
          currentInterval={
            subscription.interval as PaymentIntervalWithoutCustom2
          }
        />
      )}

      {!isSeedListPlan && (
        <>
          {canBuyMore ? (
            <BuyMoreMailboxesDialog
              currentPlan={plan}
              subscriptionId={subscription._id}
              interval={subscription.interval as PaymentIntervalWithoutCustom2}
            />
          ) : (
            <Badge
              variant="outline"
              className="text-xs bg-red-100 text-red-500 border-none w-fit"
            >
              Unpaid Invoice
            </Badge>
          )}
        </>
      )}
    </>
  );
}

const UsagesCard = ({
  name,
  unit,
  used,
  total,
}: {
  name: string;
  unit: string;
  used: number;
  total: number;
}) => {
  return (
    <Card className="bg-muted/80">
      <CardHeader className="p-3">
        <CardTitle className="text-sm font-medium">{name}</CardTitle>
      </CardHeader>
      <CardContent className="p-3 pt-0">
        <div>
          <div className="flex gap-1 text-xs mb-1">
            {/* <span>{unit} Used</span>
            <span className="font-medium">
              {used}/{total}
            </span> */}
            <span className="font-medium">{used}</span> {unit} used /{" "}
            <span className="font-medium">{total}</span> {unit}
          </div>
          <Progress value={(used / total) * 100} className="h-2" />
          <p className="text-xs text-muted-foreground mt-1">
            {total - used} {unit} remaining
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

const PaymentInfo = () => {
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<IPaymentMethod | null>(null);

  const { data: paymentMethods, isLoading } = useGetPaymentMethods();

  const { mutate: addPaymentMethod, isPending: isAddingPaymentMethod } =
    useAddPaymentMethod();

  const {
    mutateAsync: deletePaymentMethod,
    isPending: isDeletingPaymentMethod,
  } = useDeletePaymentMethod();

  const handleDeletePaymentMethod = async () => {
    try {
      if (!selectedPaymentMethod?.id) {
        toast.error("No payment method selected");
        return;
      }

      await deletePaymentMethod(selectedPaymentMethod.id);

      window.cioanalytics?.track("payment_method_deleted", {});
    } catch (error) {
      toast.error("Failed to delete payment method");
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-lg font-medium">Payment Information</h2>
          <p className="text-sm text-muted-foreground">
            Manage your payment methods.
          </p>
        </div>
        <Button
          variant="default"
          size="sm"
          disabled={isAddingPaymentMethod}
          onClick={() => {
            addPaymentMethod();
            window.cioanalytics?.track("open_customer_portal", {
              for: "payment_method",
            });
          }}
          className="w-full sm:w-auto"
        >
          {isAddingPaymentMethod ? (
            <Loader2 className="animate-spin" />
          ) : (
            <Plus />
          )}
          Add Payment Method
        </Button>
      </div>

      {isLoading && (
        <Card className="flex items-center justify-center gap-3 p-4">
          <Loader2 className="size-4 animate-spin" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </Card>
      )}

      {!isLoading &&
        (!paymentMethods || paymentMethods.paymentMethods.length === 0) && (
          <Card className="p-6 py-10 flex flex-col items-center justify-center text-center bg-secondary">
            <div className="h-12 aspect-square border rounded-2xl bg-background flex items-center justify-center mb-3">
              <CreditCard className="size-6 text-foreground" />
            </div>
            <h3 className="text-base font-medium">No Cards Added</h3>
            <p className="text-sm text-muted-foreground max-w-md">
              You haven&apos;t added any cards yet. Add a credit or debit card
              to make payments and manage your billing with ease.
            </p>

            <Button
              variant="default"
              size="sm"
              className="mt-4"
              disabled={isAddingPaymentMethod}
              onClick={() => {
                addPaymentMethod();
              }}
            >
              {isAddingPaymentMethod ? (
                <Loader2 className="animate-spin" />
              ) : (
                <Plus />
              )}
              Add Payment Method
            </Button>
          </Card>
        )}

      {!isLoading && (
        <div className="flex flex-col gap-2">
          {paymentMethods?.paymentMethods?.map((payment) => (
            <PaymentMethodCard
              key={payment.id}
              paymentMethod={payment}
              isDefault={payment.id === paymentMethods?.defaultPaymentMethodId}
              onDelete={() => {
                setSelectedPaymentMethod(payment);
                setShowDeleteConfirmation(true);
              }}
            />
          ))}
        </div>
      )}

      {showDeleteConfirmation && (
        <AlertDialog
          open={showDeleteConfirmation}
          onOpenChange={setShowDeleteConfirmation}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              {selectedPaymentMethod?.id ===
              paymentMethods?.defaultPaymentMethodId ? (
                <>
                  <AlertDialogTitle>
                    You cannot delete your default payment method
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    Please select another payment method as your default first.
                  </AlertDialogDescription>
                </>
              ) : (
                <>
                  <AlertDialogTitle>Delete Payment Method</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to delete this payment method? This
                    action cannot be undone.
                  </AlertDialogDescription>
                </>
              )}
            </AlertDialogHeader>

            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              {selectedPaymentMethod?.id !==
                paymentMethods?.defaultPaymentMethodId && (
                <AlertDialogAction onClick={handleDeletePaymentMethod}>
                  Continue
                </AlertDialogAction>
              )}
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
};

const PaymentMethodCard = ({
  paymentMethod,
  isDefault,
  onDelete,
}: {
  paymentMethod: IPaymentMethod;
  isDefault: boolean;
  onDelete: () => void;
}) => {
  const {
    mutate: updateDefaultPaymentMethod,
    isPending: isUpdatingDefaultPaymentMethod,
  } = useUpdateDefaultPaymentMethod();

  const cardNumberPrefixString = "**** **** ****";
  return (
    <Card className="flex flex-col sm:flex-row sm:items-center gap-3 p-4">
      <div className="h-10 aspect-square w-fit border rounded-md bg-secondary flex items-center justify-center sm:flex-shrink-0">
        <CreditCard className="size-5 text-muted-foreground" />
      </div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between w-full gap-4">
        <div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
            <p className="text-sm font-medium space-x-1">
              <span className="font-medium uppercase">
                {paymentMethod.brand}
              </span>
              <span>{cardNumberPrefixString}</span>
              <span>{paymentMethod.last4}</span>
            </p>
            {isDefault && (
              <Badge
                variant="outline"
                className="bg-blue-100 text-blue-500 border-none w-fit dark:bg-blue-900/20 dark:text-blue-400 dark:hover:bg-blue-900/20"
              >
                Default
              </Badge>
            )}
          </div>
          <p className="text-xs space-x-1 text-muted-foreground">
            <span>
              Expires on {paymentMethod.exp_month}/{paymentMethod.exp_year}
            </span>
          </p>
        </div>

        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
          {!isDefault && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                updateDefaultPaymentMethod(paymentMethod.id);
                window.cioanalytics?.track("payment_method_set_default", {});
              }}
              disabled={isUpdatingDefaultPaymentMethod}
              isLoading={isUpdatingDefaultPaymentMethod}
              className="w-full sm:w-auto"
            >
              Make Default
            </Button>
          )}
          <Button
            variant="secondary"
            size="icon"
            className="delete-button group sm:w-auto w-full sm:h-auto h-10"
            disabled={isUpdatingDefaultPaymentMethod}
            onClick={() => onDelete()}
          >
            <Trash className="size-4" />
            <span className="sm:hidden ml-2">Delete</span>
          </Button>
        </div>
      </div>
    </Card>
  );
};

const AddressBlock = ({
  billingDetails,
}: {
  billingDetails: IBillingDetails;
}) => {
  // Convert country code to name if available
  const formattedCountry = useMemo(() => {
    if (!billingDetails || typeof billingDetails !== "object") return null;

    const { address } = billingDetails;
    if (!address || typeof address !== "object") return null;

    const { country } = address;
    return getCountryName(country || "");
  }, [billingDetails]);

  if (!billingDetails || typeof billingDetails !== "object") return null;

  const { address, phone } = billingDetails;
  if (!address || typeof address !== "object") return "N/A";

  const { line1, line2, city, postal_code, state, country } = address;

  const lines = [
    line1,
    line2,
    [city, postal_code].filter(Boolean).join(" "),
    state,
    formattedCountry,
    phone || "",
  ].filter(Boolean);

  return (
    <div>
      {lines.length > 0 ? (
        lines.map((line, idx) => <div key={idx}>{line}</div>)
      ) : (
        <p className="text-sm text-muted-foreground">No address</p>
      )}
    </div>
  );
};

const BillingDetails = () => {
  const {
    mutateAsync: createCustomerPortalSession,
    isPending: isCreatingCustomerPortalSession,
  } = useCustomerPortal();

  const { data: paymentMethods, isLoading: isLoadingPaymentMethods } =
    useGetPaymentMethods();

  const handleOpenCustomerPortal = async () => {
    try {
      await createCustomerPortalSession();
      window.cioanalytics?.track("customer_portal_opened", {
        for: "address",
      });
    } catch (error) {
      toast.error("Failed to open customer portal");
    }
  };

  const billingDetails = paymentMethods?.billingDetails;

  return (
    <div className="space-y-2">
      <div className="flex flex-col">
        <h2 className="text-lg font-medium">Billing Details</h2>
        <p className="text-sm text-muted-foreground">
          Manage your billing information.
        </p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <p className="text-sm font-medium">Name</p>
            <p className="text-sm text-muted-foreground">
              {billingDetails?.name || "N/A"}
            </p>
          </div>

          <div className="flex flex-col gap-1">
            <p className="text-sm font-medium">Billing Email</p>
            <p className="text-sm text-muted-foreground">
              {billingDetails?.email || "N/A"}
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <p className="text-sm font-medium">Address</p>
            <div className="text-sm text-muted-foreground">
              {billingDetails ? (
                <AddressBlock billingDetails={billingDetails} />
              ) : (
                "N/A"
              )}
            </div>
          </div>
        </div>

        <div className="flex lg:justify-start">
          <Button
            variant="outline"
            className="w-full lg:w-fit"
            onClick={handleOpenCustomerPortal}
            disabled={isCreatingCustomerPortalSession}
          >
            {isCreatingCustomerPortalSession ? (
              <>
                <Loader2 className="animate-spin" />
                Redirecting to customer portal...
              </>
            ) : billingDetails?.name ||
              billingDetails?.email ||
              billingDetails?.address ? (
              "Update Billing Details"
            ) : (
              "Add Billing Details"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
